import React, { useCallback, useEffect } from 'react';

import { capitalize, debounce, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Highlight, useHits, useSearchBox } from 'react-instantsearch-hooks-web';

import SearchIcon from '../../assets/icons/SearchIcon';
import styled from '../../styled';
import { colorsPalette, SUBMIDDLE_MARGIN_PX, TINY_MARGIN_PX } from '../../theme';

const QuerySuggestions = ({
  value,
  onChange,
  onKeyPress,
  inputRef,
  withAutocomplete,
  search,
  placeholder,
}) => {
  const { t } = useTranslation();
  const { hits } = useHits<{ query: string }>();
  const { refine: refineHits } = useSearchBox();
  const validQuery = value.length > 1;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedRefineHits = useCallback(debounce(refineHits, 500), [refineHits]);

  useEffect(() => {
    if (withAutocomplete && validQuery) {
      debouncedRefineHits(value);
    }
  }, [validQuery, debouncedRefineHits, withAutocomplete, value]);

  return (
    <Autocomplete
      data-testid="searchBox"
      options={validQuery && withAutocomplete ? hits : []}
      popupIcon={null}
      // @ts-ignore
      getOptionLabel={(option) => option?.query || ''}
      disablePortal
      inputValue={value}
      onInputChange={onChange}
      onChange={(e, option) => {
        // @ts-ignore
        search(option?.query || '');
      }}
      PaperComponent={(params) => (
        // @ts-ignore
        <StyledGroup {...params} hasOptions={params.children?.filter(Boolean).length > 0} />
      )}
      freeSolo
      renderOption={(params, option) => (
        <StyledOption {...params}>
          <StyledSearchIcon />
          <Highlight hit={option} attribute="query" />
        </StyledOption>
      )}
      renderInput={(params) => (
        <StyledTextField
          {...params}
          placeholder={(placeholder && capitalize(placeholder)) || t('page.discover.searchAll')}
          inputProps={{
            ...params?.inputProps,
            'aria-label': 'enter key words',
          }}
          onKeyPress={onKeyPress}
          inputRef={inputRef}
          hasValue={value.length > 0}
        />
      )}
    />
  );
};

export default QuerySuggestions;

const StyledGroup = styled('div', {
  shouldForwardProp: (prop) => prop !== 'hasOptions',
})`
  position: relative;
  top: 5px;
  padding-top: ${TINY_MARGIN_PX};
  padding-bottom: ${TINY_MARGIN_PX};
  border-radius: 8px;
  box-shadow: 0 3px 6px ${alpha(colorsPalette.black, 0.4)};
  display: ${({ hasOptions }) => !hasOptions && 'none'};

  & .MuiAutocomplete-listbox {
    & .MuiAutocomplete-option[aria-selected='true'] {
      &.Mui-focused {
        background: ${colorsPalette.violetAnalyticsCard};
      }
    }

    &::-webkit-scrollbar {
      width: 18px;
    }

    &::-webkit-scrollbar-track {
      box-shadow: inset 0 0 18px 18px transparent;
      border: solid 7px transparent;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 18px;
      box-shadow: inset 0 0 18px 18px ${alpha(colorsPalette.blue, 0.33)};
      border: solid 7px transparent;
    }
  }
`;
const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'hasValue',
})`
  width: 100%;

  & .MuiAutocomplete-input {
    padding-left: ${({ hasValue }) => (hasValue ? SUBMIDDLE_MARGIN_PX : 0)} !important;
    transition: padding-left 0.2s;
  }

  & .MuiOutlinedInput-root {
    padding-left: 0;

    & fieldset {
      border-color: transparent;
    }

    &:hover fieldset {
      border-color: transparent;
    }

    &.Mui-focused fieldset {
      border-color: transparent;
    }
  }
`;
const StyledOption = styled('div')`
  & mark {
    color: ${colorsPalette.white};
    background: ${colorsPalette.blue};
  }
`;
const StyledSearchIcon = styled(SearchIcon)`
  width: 14px;
  height: 14px;
  margin-right: ${TINY_MARGIN_PX};
`;
